import { html } from 'htm/preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { NewsListItem } from './NewsListItem.js'
import { LoadingSpinner } from './LoadingSpinner.js'
import { api } from '../services/api.js'

export function NewsList({ selectedSources, sources, setSources, onArticlesUpdated, isAuthenticated, onLoginClick }) {
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [newCount, setNewCount] = useState(0)
  const anchorIdRef = useRef(null)
  const prevSourcesRef = useRef(undefined)

  // Load sources on component mount
  useEffect(() => {
    loadSources()
  }, [])

  // Load articles when sources or page changes
  useEffect(() => {
    const prevSources = prevSourcesRef.current
    prevSourcesRef.current = selectedSources

    // If sources changed, reset anchor and go to page 1
    const sourcesChanged = prevSources !== undefined &&
      JSON.stringify(prevSources) !== JSON.stringify(selectedSources)

    if (sourcesChanged) {
      anchorIdRef.current = null
      setNewCount(0)
      if (page !== 1) {
        setPage(1)
        return // useEffect will re-fire when page updates
      }
    }

    loadArticles()
  }, [selectedSources, page])

  // Periodically check for new articles and refresh sources
  // On page 1: auto-refresh the list. On other pages: just show a banner.
  useEffect(() => {
    const interval = setInterval(() => {
      if (page === 1) {
        anchorIdRef.current = null
        loadArticles()
      } else {
        checkNewArticles()
      }
      loadSources()
      if (onArticlesUpdated) onArticlesUpdated()
    }, 30000)

    return () => clearInterval(interval)
  }, [selectedSources, page])

  const loadSources = async () => {
    try {
      const response = await api.get('/sources/')
      setSources(response.data.sources)
    } catch (error) {
      console.error('Error loading sources:', error)
    }
  }

  const loadArticles = async () => {
    if (selectedSources === null || selectedSources.length === 0) {
      setArticles([])
      setTotal(0)
      setTotalPages(0)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const params = {
        page,
        per_page: 50,
        source_ids: selectedSources
      }

      if (anchorIdRef.current) {
        params.anchor_id = anchorIdRef.current
      }

      const response = await api.get('/articles/', { params })
      const data = response.data

      setArticles(data.articles)
      setTotal(data.total)
      setTotalPages(data.total_pages)

      // Capture anchor from first load (highest ID = newest article)
      if (!anchorIdRef.current && data.articles.length > 0) {
        anchorIdRef.current = data.articles[0].id
      }
    } catch (error) {
      console.error('Error loading articles:', error)
      setError('Eroare la incarcarea stirilor. Incercati din nou.')
    } finally {
      setLoading(false)
    }
  }

  const checkNewArticles = async () => {
    if (!anchorIdRef.current || !selectedSources?.length) return
    try {
      const response = await api.get('/articles/new-count', {
        params: { after_id: anchorIdRef.current, source_ids: selectedSources }
      })
      setNewCount(response.data.count)
    } catch (e) {
      // Silently ignore
    }
  }

  const showNewArticles = () => {
    anchorIdRef.current = null
    setNewCount(0)
    if (page === 1) {
      loadArticles()
    } else {
      setPage(1)
    }
  }

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Generate page numbers with ellipsis for large ranges
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const pages = [1]

    if (page > 3) pages.push('...')

    const start = Math.max(2, page - 1)
    const end = Math.min(totalPages - 1, page + 1)
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (page < totalPages - 2) pages.push('...')

    pages.push(totalPages)

    return pages
  }

  if (selectedSources === null || (loading && articles.length === 0)) {
    return html`
      <div class="flex justify-center items-center py-12">
        <${LoadingSpinner} />
      </div>
    `
  }

  if (error) {
    return html`
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <p class="text-red-600 dark:text-red-400 mb-4">${error}</p>
        <button
          onClick=${showNewArticles}
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Incercati din nou
        </button>
      </div>
    `
  }

  return html`
    <div class="flex-grow">
      <!-- Header -->
      <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
              ${selectedSources !== null && selectedSources.length > 0
                ? `${selectedSources.length} ${selectedSources.length === 1 ? 'sursa selectata' : 'surse selectate'}`
                : 'Nicio sursa selectata'}
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              ${total} ${total === 1 ? 'articol' : 'articole'}
              ${totalPages > 1 ? ` — pagina ${page} din ${totalPages}` : ''}
            </p>
          </div>

          <div class="flex items-center gap-3">
            <button
              onClick=${showNewArticles}
              class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700
                     transition-colors flex items-center gap-2"
              disabled=${loading}
            >
              <svg class="w-4 h-4 ${loading ? 'animate-spin' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Actualizeaza
            </button>
          </div>
        </div>
      </div>

      <!-- New articles banner -->
      ${newCount > 0 ? html`
        <div class="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-6 py-3">
          <button
            onClick=${showNewArticles}
            class="w-full text-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
          >
            ${newCount} ${newCount === 1 ? 'articol nou' : 'articole noi'} — click pentru a actualiza
          </button>
        </div>
      ` : null}

      <!-- Articles List -->
      <div class="bg-white dark:bg-gray-800">
        ${articles.length === 0 ? html`
          <div class="text-center py-12">
            ${selectedSources !== null && selectedSources.length === 0 ? html`
              <svg class="mx-auto h-12 w-12 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 18.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
              <p class="mt-2 text-orange-600 dark:text-orange-400 text-lg font-medium">
                Nicio sursa selectata
              </p>
              <p class="mt-1 text-gray-500 dark:text-gray-400">
                Selectati cel putin o sursa din bara laterala pentru a vedea articolele.
              </p>
            ` : html`
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p class="mt-2 text-gray-500 dark:text-gray-400 text-lg">
                Nu au fost gasite articole pentru sursele selectate.
              </p>
            `}
          </div>
        ` : html`
          <div class="divide-y divide-gray-200 dark:divide-gray-700 ${loading ? 'opacity-60 pointer-events-none' : ''} transition-opacity">
            ${articles.map(article => {
              const articleSource = sources.find(s => s.id === article.source_id)
              return html`<${NewsListItem}
                key=${article.id}
                article=${article}
                source=${articleSource}
                isAuthenticated=${isAuthenticated}
                onLoginClick=${onLoginClick}
              />`
            })}
          </div>
        `}

        <!-- Pagination -->
        ${totalPages > 1 ? html`
          <div class="flex items-center justify-center gap-1 py-6 px-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick=${() => goToPage(page - 1)}
              disabled=${page === 1 || loading}
              class="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600
                     text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Anterioara
            </button>

            ${getPageNumbers().map(p =>
              p === '...'
                ? html`<span key=${'dots-' + Math.random()} class="px-2 py-2 text-gray-400 dark:text-gray-500 select-none">…</span>`
                : html`
                  <button
                    key=${p}
                    onClick=${() => goToPage(p)}
                    disabled=${loading}
                    class="px-3 py-2 text-sm rounded-lg transition-colors
                           ${p === page
                             ? 'bg-primary-600 text-white font-medium'
                             : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                           } disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ${p}
                  </button>
                `
            )}

            <button
              onClick=${() => goToPage(page + 1)}
              disabled=${page === totalPages || loading}
              class="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600
                     text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Urmatoarea
            </button>
          </div>
        ` : null}

        <!-- Loading indicator for page transitions -->
        ${loading && articles.length > 0 ? html`
          <div class="flex justify-center py-4">
            <${LoadingSpinner} size="small" />
          </div>
        ` : null}
      </div>
    </div>
  `
}
